import React, { useEffect, useRef } from 'react'
import { Col, Container, Modal, Row } from 'react-bootstrap'
import ATTextButton from '../common/formComponent/ATTextButton'
import ATTextWithLabel from '../common/formComponent/ATTextWithLabel'
import { plantAddress, plantBasicDetails } from './PlantConstants'
import ATCheckBox from '../common/formComponent/ATCheckbox'
import { useTranslation } from 'react-i18next'
import { PlantInfo } from '../../interface/response/PlantResponse'
import ATLoaderContainer from '../common/loaderContainer/ATLoaderContainer'
import ATModuleMessage from '../common/message/ATModuleMessage'
import { REQUEST_STATUS } from '../../utils/constants/RequestCommonConstant'
import { BsExclamationTriangle } from 'react-icons/bs'

import ATCommentBox from '../common/rejectBox/ATCommentBox'
import usePlantDetails from '../../customHooks/plant/usePlantDetails'
import ATConfirmBox from '../common/confirmBox/ATConfirmBox'
import PlantRejectForm from './PlantRejectForm'
import PlantDetailsForm from './PlantDetailsForm'
import ATMap from '../common/map/ATMap'
import { PERMISSIONS, WIDGET_TYPE } from '../common/auth/AuthorizeConstant'
import { getWidgetMapping, isAuthorized } from '../../utils/authUtil/AuthUtil'
import Authorize from '../common/auth/Authorize'

interface PlantDetailsProps {
  data?: PlantInfo
  setSelectedPlant: (val: any) => void
}

const PlantDetails: React.FC<PlantDetailsProps> = ({
  data,
  setSelectedPlant,
}: PlantDetailsProps) => {
  const mapRef: any = useRef()
  const { t } = useTranslation(['translation'])
  const {
    verifiedDetails,
    showRejectPlantAlert,
    showApprovePlantAlert,
    showPlantDetailsForm,
    updatePlantFormStatus,
    getFormItemValue,
    handleOnClickReject,
    handleOnClickApprove,
    approvePlantStatus,
    rejectPlantStatus,
    setShowRejectPlantAlert,
    setShowApprovePlantAlert,
    updateVerifiedCheckBoxStatus,
  } = usePlantDetails()

  useEffect(() => {
    if (data) {
      updateVerifiedCheckBoxStatus(false)
      if (mapRef?.current) {
        mapRef?.current?.getMapLocation(data?.latitude, data?.longitude)
      }
    }
  }, [data])

  const isLatLongValid = () => {
    return data?.latitude !== 0 && data?.longitude !== 0 ? true : false
  }

  const getJsxConditionally = () => {
    if (!data) {
      return (
        <ATLoaderContainer>
          <ATModuleMessage />
        </ATLoaderContainer>
      )
    }
    return (
      <Container fluid className="plant-details-container p-2">
        <Row className="px-2">
          <Col xs={12} className="mt-3">
            <Row className="align-items-center mx-2 px-0 pb-1 border-bottom">
              <Col className="px-0">
                <p className="headers my-0">{t('common.basic_details')}</p>
              </Col>
              {data?.plantCode &&
                !(data?.status === REQUEST_STATUS.REJECTED) && (
                  <Col className="text-end px-0">
                    <Authorize
                      widgetId={getWidgetMapping(
                        PERMISSIONS.EDIT_PLANT_DETAILS,
                      )}
                      widgetType={WIDGET_TYPE.BUTTON}
                    >
                      <ATTextButton
                        isFilledButton={false}
                        title={t('common.edit')}
                        onPressHandler={() => updatePlantFormStatus(true)}
                      />
                    </Authorize>
                  </Col>
                )}
            </Row>
          </Col>
          {/* Plant Basic Details */}
          <Col xs={12} className="mt-3">
            <Row className="py-2 justify-content-around position-relative">
              {data?.vendorCode ? (
                <>
                  <Row className="py-2 justify-content-between position-relative">
                    {plantBasicDetails.map(
                      (formItem: { key: string; label: string }, index) => (
                        <>
                          <Col
                            key={formItem.key}
                            className={`py-3 ${
                              formItem?.key && 'border-bottom'
                            }`}
                            xs={5}
                          >
                            {formItem && (
                              <ATTextWithLabel
                                label={formItem?.label}
                                value={
                                  getFormItemValue(data, formItem?.key) ?? '--'
                                }
                              />
                            )}
                          </Col>
                        </>
                      ),
                    )}
                  </Row>
                  {isLatLongValid() && (
                    <Row className="py-2 justify-content-between position-relative plant-map">
                      <ATMap
                        latitude={data?.latitude}
                        longitude={data?.longitude}
                        radius={data?.radius}
                        plantName={data?.plantName}
                        ref={mapRef}
                      />
                    </Row>
                  )}
                  <Row className="py-2 justify-content-between position-relative">
                    <Col className="px-2 mx-2 my-3 pb-1 border-bottom col-12">
                      <p className="headers my-0">{t('plant.plant_address')}</p>
                    </Col>
                    {plantAddress.map(
                      (formItem: { key: string; label: string }, index) => (
                        <>
                          <Col
                            key={formItem.key}
                            className={`py-3 ${
                              formItem?.key && 'border-bottom'
                            }`}
                            xs={5}
                          >
                            {formItem && (
                              <ATTextWithLabel
                                label={formItem?.label}
                                value={
                                  getFormItemValue(data, formItem?.key) ?? '--'
                                }
                              />
                            )}
                          </Col>
                        </>
                      ),
                    )}
                  </Row>
                </>
              ) : (
                <ATModuleMessage icon={<BsExclamationTriangle />} />
              )}
            </Row>
          </Col>
          {data?.status === REQUEST_STATUS.REJECTED && (
            <ATCommentBox message={data?.reasonDescription ?? ''} />
          )}
          {data?.status === REQUEST_STATUS.PENDING &&
            isAuthorized(PERMISSIONS.REVIEW_PLANT) && (
              <>
                <Col xs={12} className="px-4 mt-3">
                  <ATCheckBox
                    label={t('common.verifiedMessage')}
                    handleChange={() => {
                      updateVerifiedCheckBoxStatus(!verifiedDetails)
                    }}
                    selectedValue={verifiedDetails}
                    checked={verifiedDetails}
                    className="plant-confirmation-check"
                  />
                </Col>
                <div className="d-flex px-0 py-3 justify-content-end">
                  <div>
                    <Authorize
                      widgetId={getWidgetMapping(PERMISSIONS.REVIEW_PLANT)}
                      widgetType={WIDGET_TYPE.DROPDOWN_ITEM}
                    >
                      <ATTextButton
                        title={t('common.reject')}
                        onPressHandler={handleOnClickReject}
                        isFilledButton={false}
                        customClassName="text-uppercase"
                      />
                    </Authorize>
                  </div>
                  <div>
                    <Authorize
                      widgetId={getWidgetMapping(PERMISSIONS.REVIEW_PLANT)}
                      widgetType={WIDGET_TYPE.DROPDOWN_ITEM}
                    >
                      <ATTextButton
                        title={t('common.approve')}
                        onPressHandler={handleOnClickApprove}
                        disabled={!verifiedDetails}
                        customClassName="text-uppercase"
                      />
                    </Authorize>
                  </div>
                </div>
              </>
            )}
        </Row>
      </Container>
    )
  }

  return (
    <div className="container-fluid h-100 p-2  scroll-list-content">
      {getJsxConditionally()}
      <ATConfirmBox
        showModal={showApprovePlantAlert}
        modalMessage={t('modal.approval_message', { property: 'plant' })}
        modalTitle={t('modal.confirm')}
        onSave={() => {
          approvePlantStatus(data?.plantCode ?? '')
          setSelectedPlant(undefined)
        }}
        onCancel={() => setShowApprovePlantAlert(false)}
      />
      <Modal show={showRejectPlantAlert}>
        <PlantRejectForm
          initialValues={{
            plantCode: data?.plantCode ?? '',
            status: REQUEST_STATUS.REJECTED,
            reasonDescription: '',
          }}
          onSave={rejectPlantStatus}
          setSelectedPlant={setSelectedPlant}
          onCloseModal={() => setShowRejectPlantAlert(false)}
        />
      </Modal>
      <Modal show={showPlantDetailsForm} size={'lg'}>
        <PlantDetailsForm
          isEdit={true}
          onCloseModal={() => updatePlantFormStatus(false)}
          data={data}
        />
      </Modal>
    </div>
  )
}
export default PlantDetails
